import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import DataTable from '../components/DataTable';
import { Button, Modal, FormField, ListPageHeader } from '../components/ui';
import { CardHero, CardStatusBadge, CardSplitButton } from '../components/card';
import { TAB_ICONS } from '../utils/tabIcons';

const ACTION_LABELS = {
  view: 'Görüntüle',
  create: 'Oluştur',
  edit: 'Düzenle',
  delete: 'Sil',
};
const ALL_ACTIONS = ['view', 'create', 'edit', 'delete'];

const emptyRole = {
  name: '', description: '',
  permissions: {}, // flat: { 'invoices.view': true, ... }
  isAll: false,
  is_active: true,
};

// Backend permissions JSON'ı çeşitli formatta gelebilir → flat formata çevir
function normalizePermissions(raw, modules) {
  if (!raw) return { isAll: false, flat: {} };
  let p = raw;
  try { if (typeof p === 'string') p = JSON.parse(p); } catch { return { isAll: false, flat: {} }; }
  if (!p || typeof p !== 'object') return { isAll: false, flat: {} };
  if (p.all === true) return { isAll: true, flat: {} };

  const flat = {};
  for (const [k, v] of Object.entries(p)) {
    if (k === 'all') continue;
    if (k.includes('.')) {
      if (v === true) flat[k] = true;
    } else {
      const mod = modules.find(m => m.key === k);
      if (!mod) continue;
      if (v === true) mod.actions.forEach(a => flat[`${k}.${a}`] = true);
      else if (Array.isArray(v)) v.forEach(a => mod.actions.includes(a) && (flat[`${k}.${a}`] = true));
    }
  }
  return { isAll: false, flat };
}

export default function RolesPage() {
  const [list, setList] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const [modalMode, setModalMode] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ ...emptyRole });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [r, m] = await Promise.all([
        api.get('/roles'),
        api.get('/auth/permission-modules'),
      ]);
      if (r.data.success) setList(r.data.data || []);
      if (m.data.success) setModules(m.data.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openAdd = () => {
    setForm({ ...emptyRole });
    setSelected(null);
    setMsg(null);
    setModalMode('add');
  };

  const openEdit = async (r) => {
    try {
      const res = await api.get('/roles/' + r.id);
      if (res.data.success) {
        const d = res.data.data;
        const { isAll, flat } = normalizePermissions(d.permissions, modules);
        setForm({
          name: d.name || '',
          description: d.description || '',
          permissions: flat,
          isAll,
          is_active: d.is_active !== false,
        });
        setSelected(d);
        setMsg(null);
        setModalMode('edit');
      }
    } catch (e) { console.error(e); }
  };

  const closeModal = () => { setModalMode(null); setSelected(null); setMsg(null); };
  const tf = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const togglePerm = (key) => setForm(prev => {
    const next = { ...prev.permissions };
    if (next[key]) delete next[key]; else next[key] = true;
    return { ...prev, permissions: next };
  });

  const setModuleAll = (modKey, actions, on) => setForm(prev => {
    const next = { ...prev.permissions };
    actions.forEach(a => {
      const k = `${modKey}.${a}`;
      if (on) next[k] = true; else delete next[k];
    });
    return { ...prev, permissions: next };
  });

  const isModuleAll = (modKey, actions) => actions.every(a => form.permissions[`${modKey}.${a}`]);
  const isModuleSome = (modKey, actions) => actions.some(a => form.permissions[`${modKey}.${a}`]);

  const handleSave = async (mode = 'close') => {
    if (mode && typeof mode === 'object') mode = 'close';
    if (!form.name.trim()) return setMsg({ type: 'error', text: 'Rol adı zorunlu' });
    setSaving(true); setMsg(null);
    try {
      const permissions = form.isAll ? { all: true } : form.permissions;
      const body = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        permissions,
        is_active: form.is_active,
      };
      if (modalMode === 'edit') await api.put('/roles/' + selected.id, body);
      else await api.post('/roles', body);
      await fetchAll();
      if (mode === 'close') closeModal();
      else if (mode === 'new') { setSelected(null); setForm({ name: '', description: '', isAll: false, permissions: {}, is_active: true }); setModalMode('add'); setMsg(null); }
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Kaydedilemedi' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!selected) return;
    if (selected.is_system) return setMsg({ type: 'error', text: 'Sistem rolü silinemez' });
    if (!window.confirm(`"${selected.name}" rolü silinecek. Devam edilsin mi?`)) return;
    try {
      await api.delete('/roles/' + selected.id);
      closeModal();
      fetchAll();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Silinemedi' });
    }
  };

  const filtered = list.filter(r =>
    !search ||
    (r.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (r.description || '').toLowerCase().includes(search.toLowerCase())
  );

  const grantedCount = form.isAll ? 'Tümü' : Object.keys(form.permissions).length;

  // Modal aksiyonları — footer'a taşındı (yeni Modal API)

  return (
    <div className="stocks-page">
      <ListPageHeader
        title="Roller"
        count={filtered.length}
        unit="rol"
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Rol adı ara..."
        onRefresh={fetchAll}
        refreshing={loading}
        actions={<Button size="sm" onClick={openAdd}>+ Yeni Rol</Button>}
      />

      <div className="stocks-table-wrap">
        <DataTable
          pageKey="roles-list"
          tableClassName="stocks-table"
          rowClassName="stocks-table-row"
          loading={loading}
          emptyText="Rol bulunamadı"
          data={filtered}
          rowKey="id"
          onRowClick={r => openEdit(r)}
          columns={[
            { id: 'name', header: 'Rol', accessor: 'name', sortable: true, width: 180,
              cell: r => <span className="stock-code">{r.name}</span> },
            { id: 'description', header: 'Açıklama', accessor: 'description', sortable: true,
              cell: r => <span className="stock-secondary">{r.description || '—'}</span> },
            { id: 'is_system', header: 'Sistem', accessor: r => r.is_system ? 1 : 0, sortable: true, width: 100,
              headerStyle: { textAlign: 'center' }, cellStyle: { textAlign: 'center' },
              cell: r => r.is_system ? <span className="stock-badge active" style={{background:'rgba(99,102,241,0.1)', color:'var(--accent-primary)'}}>Sistem</span> : '—' },
            { id: 'user_count', header: 'Kullanıcı', accessor: r => r.user_count || 0, sortable: true, width: 100,
              headerStyle: { textAlign: 'center' }, cellStyle: { textAlign: 'center' } },
            { id: 'status', header: 'Durum', accessor: r => r.is_active ? 'Aktif' : 'Pasif', sortable: true, width: 100,
              headerStyle: { textAlign: 'center' }, cellStyle: { textAlign: 'center' },
              cell: r => <span className={'stock-badge ' + (r.is_active ? 'active' : 'passive')}>{r.is_active ? 'Aktif' : 'Pasif'}</span> },
          ]}
        />
      </div>

      {modalMode !== null && (
        <Modal
          icon={null}
          title={modalMode === 'add' ? 'Yeni Rol' : 'Rol'}
          size="xl"
          onClose={closeModal}
          footer={
            <>
              <div className="ui-modal-footer-left">
                {modalMode === 'edit' && !selected?.is_system && (
                  <Button variant="danger" onClick={handleDelete}>🗑 Sil</Button>
                )}
              </div>
              <div className="ui-modal-footer-right" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <Button variant="ghost" onClick={closeModal}>{modalMode === 'add' ? 'Vazgeç' : 'İptal'}</Button>
                <CardSplitButton
                  primaryLabel="💾 Kaydet"
                  primaryAction={() => handleSave('close')}
                  loading={saving}
                  options={[
                    { label: 'Kaydet ve Yeni Aç', icon: '✚', onClick: () => handleSave('new') },
                    { label: 'Kaydet ve Kapat',  icon: '✓', onClick: () => handleSave('close') },
                  ]}
                />
              </div>
            </>
          }
        >
          {modalMode === 'edit' && (
            <CardHero
              icon="🛡"
              title={selected?.name || form.name}
              subtitle={form.description}
              statusBadge={
                selected?.is_system
                  ? <CardStatusBadge label="Sistem" color="#7c3aed" bg="rgba(124,58,237,0.15)" />
                  : <CardStatusBadge preset="active" label="Özel" />
              }
              kpis={[
                { label: 'İzin Sayısı', value: form.isAll ? 'Tümü' : Object.keys(form.permissions).length, color: 'primary' },
                form.user_count > 0 && { label: 'Kullanıcı', value: form.user_count, color: 'info' },
              ].filter(Boolean)}
            />
          )}
          <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', marginBottom: 12 }}>
            İzinli işlem: <strong>{grantedCount}</strong>
          </div>

          {msg && (
            <div style={{
              marginBottom: 12, padding: '9px 12px', borderRadius: 8, fontSize: '0.84rem',
              background: msg.type === 'error' ? 'var(--danger-bg)' : 'rgba(16,185,129,0.1)',
              color: msg.type === 'error' ? 'var(--danger)' : '#0d9488',
              border: '1px solid ' + (msg.type === 'error' ? 'rgba(239,68,68,0.25)' : 'rgba(16,185,129,0.25)'),
            }}>{msg.text}</div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 16 }}>
            <FormField label="Rol Adı" required hint={selected?.is_system ? 'Sistem rollerinin adı değiştirilemez' : null}>
              <input value={form.name} onChange={e => tf('name', e.target.value)} disabled={selected?.is_system} maxLength={50}/>
            </FormField>
            <FormField label="Durum">
              <select value={form.is_active ? '1' : '0'} onChange={e => tf('is_active', e.target.value === '1')}>
                <option value="1">Aktif</option>
                <option value="0">Pasif</option>
              </select>
            </FormField>
            <FormField label="Açıklama" fullWidth>
              <input value={form.description} onChange={e => tf('description', e.target.value)} maxLength={200}/>
            </FormField>
          </div>

          <div style={{padding:'10px 12px', background:'rgba(220,38,38,0.05)', border:'1px solid rgba(220,38,38,0.2)', borderRadius:8, marginBottom:12}}>
            <label style={{display:'flex', gap:8, alignItems:'center', cursor:'pointer'}}>
              <input type="checkbox" checked={form.isAll} onChange={e => tf('isAll', e.target.checked)}/>
              <div>
                <div style={{fontWeight:600, fontSize:'0.85rem', color:'#dc2626'}}>Tüm Yetkiler (superuser)</div>
                <div style={{fontSize:'0.75rem', color:'var(--text-tertiary)', marginTop:2}}>
                  Bu rol her modüldeki her işlemi yapabilir. Detaylı seçim devre dışı kalır.
                </div>
              </div>
            </label>
          </div>

          {!form.isAll && (
            <div style={{border:'1px solid var(--border-secondary)', borderRadius:8, overflow:'hidden'}}>
              <table style={{width:'100%', borderCollapse:'collapse', fontSize:'0.85rem'}}>
                <thead style={{background:'var(--bg-secondary)', position:'sticky', top:0}}>
                  <tr>
                    <th style={{padding:'8px 12px', textAlign:'left', width:'40%'}}>Modül</th>
                    {ALL_ACTIONS.map(a => (
                      <th key={a} style={{padding:'8px 8px', textAlign:'center', width:'13%'}}>{ACTION_LABELS[a]}</th>
                    ))}
                    <th style={{padding:'8px 8px', textAlign:'center', width:'8%'}}>Tümü</th>
                  </tr>
                </thead>
                <tbody>
                  {modules.map(m => {
                    const allOn = isModuleAll(m.key, m.actions);
                    const someOn = isModuleSome(m.key, m.actions);
                    return (
                      <tr key={m.key} style={{borderTop:'1px solid var(--border-secondary)', background: allOn ? 'rgba(99,102,241,0.04)' : 'transparent'}}>
                        <td style={{padding:'8px 12px', fontWeight:500}}>{m.label}</td>
                        {ALL_ACTIONS.map(a => {
                          const supported = m.actions.includes(a);
                          const key = `${m.key}.${a}`;
                          return (
                            <td key={a} style={{textAlign:'center', padding:'4px'}}>
                              {supported ? (
                                <input type="checkbox" checked={!!form.permissions[key]} onChange={() => togglePerm(key)}/>
                              ) : <span style={{color:'var(--text-tertiary)', fontSize:'0.75rem'}}>—</span>}
                            </td>
                          );
                        })}
                        <td style={{textAlign:'center', padding:'4px'}}>
                          <input type="checkbox"
                            checked={allOn}
                            ref={el => el && (el.indeterminate = someOn && !allOn)}
                            onChange={(e) => setModuleAll(m.key, m.actions, e.target.checked)}
                            title="Modülün tüm aksiyonlarını seç/kaldır"/>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}
